import { useState, useEffect } from "react";
import { QuestionCard } from "./question-card";
import { QuestionType } from "@/types/question";
import type { Question } from "./question";
import { Card } from "@radix-ui/themes";
import { PracticeTestDetail } from "@/types/practice-test";
import { questionService } from "@/services/questionsService";
import { filterCaseStudyQuestionsByQuestionIds } from "@/utils/practiceTestSectionIds";
import { buildSingleSubQuestionText } from "@/utils/question-text";

interface QuestionListProps {
  practiceTestData?: PracticeTestDetail;
}

export default function QuestionList({ practiceTestData }: QuestionListProps) {
  const [activeRegradeId, setActiveRegradeId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);

  useEffect(() => {
    let isCancelled = false;
    async function build() {
      if (!practiceTestData?.sections) {
        if (!isCancelled) setQuestions([]);
        return;
      }
      const result: Question[] = [];
      let questionCounter = 1;

      for (const section of practiceTestData.sections) {
        // Normal questions: if only 1 sub-question, render directly; if >1, nest as CaseStudy
        for (const questionDetail of section.questions.data) {
          const subs = Array.isArray(questionDetail.sub_questions)
            ? questionDetail.sub_questions
            : [];

          if (subs.length === 1) {
            const subQ = subs[0];
            result.push({
              id: `q-${questionDetail.question_id ?? questionCounter++}`,
              question: buildSingleSubQuestionText(
                questionDetail.text || questionDetail.question_text,
                subQ.text,
              ),
              section: section.name,
              point: subQ.marks,
              type: subQ.type,
              options: 
                subQ.type === QuestionType.multiple_choice || subQ.type === QuestionType.single_choice
                  ? (subQ.options || []).map((o: any) =>
                      typeof o === "string" ? o : o?.text ?? o?.option_text ?? ""
                    )
                  : undefined,
            });
          } else if (subs.length > 1) {
            const subQuestions = subs.map((subQ: any) => ({
              id: `sub-${questionCounter++}`,
              question: subQ.text || "",
              point: subQ.marks,
              type: subQ.type,
              options:
                subQ.type === QuestionType.multiple_choice || subQ.type === QuestionType.single_choice
                  ? (subQ.options || []).map((o: any) =>
                      typeof o === "string" ? o : o?.text ?? o?.option_text ?? ""
                    )
                  : undefined,
            }));

            result.push({
              // Ensure parent id is unique and not overlapping with sub ids
              id: `q-${questionDetail.question_id ?? questionCounter}`,
              question:
                questionDetail.text || questionDetail.question_text || "",
              section: section.name,
              // aggregate points (optional)
              point: subQuestions.reduce(
                (acc, s) => acc + (Number(s.point) || 0),
                0
              ),
              type: QuestionType.CaseStudy,
              subQuestions,
            });
          } else {
            // Fallback: no sub-questions, render as a single paragraph question
            result.push({
              id: `q-${questionDetail.question_id ?? questionCounter++}`,
              question:
                questionDetail.text || questionDetail.question_text || "",
              section: section.name,
              point: 0,
              type: QuestionType.text,
            });
          }
        }

        // Case studies from separate endpoint
        const csList = Array.isArray(section.case_studies?.data)
          ? section.case_studies?.data
          : section.case_studies?.data
          ? [section.case_studies.data]
          : [];
        for (const cs of csList as any[]) {
          if (!cs?.case_study_id) continue;
          try {
            const resp = await questionService.getQuestionByCaseStudy(
              cs.case_study_id
            );
            const list = filterCaseStudyQuestionsByQuestionIds(
              (resp.questions || resp.question || []) as { question_id: number }[],
              cs.question_ids
            );
            // Prefer content returned alongside questions; fallback to fields on the section item
            const csContent =
              (resp as any)?.case_study_content ||
              (cs.case_study || cs.content || "");
            // Build hierarchical structure: Case Study -> Big Questions -> Sub-Questions
            const bigQuestions = list.map((q: any, idx: number) => {
              const subs = Array.isArray(q.sub_questions_details)
                ? q.sub_questions_details
                : [];

              const bigQuestionId = `cs-${cs.case_study_id}-q-${q.question_id ?? idx}`;

              if (!subs.length) {
                // Big question without explicit sub-questions
                return {
                  id: bigQuestionId,
                  question: q.text || "",
                  point: Number(q.marks) || 0,
                  type: QuestionType.text,
                  section: section.name,
                  subQuestions: [] as Question[],
                } as Question;
              }

              const subQuestions = subs.map((sub: any, sidx: number) => ({
                id: `${bigQuestionId}-sub-${sub.sub_question_id ?? sidx}`,
                question: sub.text || "",
                point: Number(sub.marks) || 0,
                type: sub.type,
                options:
                  sub.type === QuestionType.multiple_choice || sub.type === QuestionType.single_choice
                    ? (sub.options || []).map((o: any) =>
                        typeof o === "string" ? o : o?.text ?? o?.option_text ?? ""
                      )
                    : undefined,
                section: section.name,
              })) as Question[];

              const bigPoint = subQuestions.reduce(
                (acc, s) => acc + (Number(s.point) || 0),
                0
              );

              // Set big question as CaseStudy type so it can expand to reveal its sub-questions
              return {
                id: bigQuestionId,
                question: q.text || "",
                point: bigPoint,
                type: QuestionType.CaseStudy,
                section: section.name,
                subQuestions,
              } as unknown as Question;
            });

            if (bigQuestions.length) {
              result.push({
                id: `cs-${cs.case_study_id}`,
                question: csContent,
                section: section.name,
                point: bigQuestions.reduce((acc, bq) => acc + (Number(bq.point) || 0), 0),
                type: QuestionType.CaseStudy,
                subQuestions: bigQuestions as unknown as Question[],
              });
            }
          } catch (_e) {
            // ignore one case study failure
          }
        }
      }

      if (!isCancelled) setQuestions(result);
    }
    build();
    return () => {
      isCancelled = true;
    };
  }, [practiceTestData]);

  // Group questions by section
  const questionsBySection = questions.reduce((acc, question) => {
    const section = question.section || "A";
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(question);
    return acc;
  }, {} as Record<string, Question[]>);

  // Get question number across all sections
  const getGlobalQuestionNumber = (questionId: string) => {
    return questions.findIndex((q) => q.id === questionId) + 1;
  };

  return (
    <div className="mx-auto w-full space-y-6">
      {Object.entries(questionsBySection).map(([sectionName, questions]) => (
        <Card key={sectionName} className="p-0">
          <div className="px-52 pt-4">
            <h2 className="text-2xl font-semibold text-[#002652] inline-block">
              Section {sectionName}
            </h2>
          </div>
          <div className="space-y-4 px-52">
            {questions.map((question) => {
              const containsActive = question.subQuestions?.some(
                (sq) => sq.id === activeRegradeId
              );
              const isDimmed =
                activeRegradeId !== null &&
                !(activeRegradeId === question.id || containsActive);

              return (
                <QuestionCard
                  key={question.id}
                  question={question}
                  questionNumber={getGlobalQuestionNumber(question.id)}
                  isActive={activeRegradeId === question.id}
                  isDimmed={isDimmed}
                  activeId={activeRegradeId}
                  setActiveId={setActiveRegradeId}
                  onRegrade={() => {
                    if (activeRegradeId) return;
                    setActiveRegradeId(question.id);
                  }}
                  onCancel={() => setActiveRegradeId(null)}
                  onSave={() => setActiveRegradeId(null)}
                />
              );
            })}
          </div>
        </Card>
      ))}
    </div>
  );
}
